import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import type { Clinician } from '@/auth/store';
import type { ClinicResponse } from '@/api/hospital';
import {
  hasStaleUserSession,
  identifyUser,
  parameterizePath,
  resetUser,
  sanitizeSegmentation,
  trackEvent,
} from './countly';

/**
 * Lightweight wrapper around `trackEvent` that sanitizes properties
 * before forwarding. Mirrors v1's `track()` helper in analytics.ts.
 * Always swallow errors — analytics must never crash the app.
 */
export const track = (
  eventName: string,
  properties: Record<string, unknown> = {},
): void => {
  try {
    trackEvent(eventName, sanitizeSegmentation(properties));
  } catch {
    // Analytics is non-critical.
  }
};

/**
 * Canonical event names. Mirrors v1 (`clinician/src/analytics.ts:113-156`)
 * so dashboards keyed off the v1 names work for the migrated V2 events
 * without dashboard surgery.
 *
 * Kept as a single object (rather than per-feature constants) so the
 * list is easy to audit and grep. Add new entries in alphabetical
 * order within a section to keep diffs clean.
 */
export const ANALYTICS_EVENTS = {
  // Session
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  HELP_CLICKED: 'Email Support Button Clicked',

  // Patient list
  PATIENT_LIST_FILTER_USED: 'Patient List Filter Used',
  PATIENT_RESEND_INVITE_CLICKED: 'Patient Resend Invite Email Clicked',
  PATIENT_DELETED: 'Patient Deleted',
  PATIENT_MOVED_CLINIC: 'Patient Moved Clinic',
  PATIENT_ADDED: 'New Patient Added',
  PATIENT_EDITED: 'Patient Edited',
  CLINICIAN_ADDED: 'New Clinician Added',
  CLINICIAN_PROFILE_EDITED: 'Clinician Profile Edited',

  // Patient view + tabs
  PATIENT_LOGGING_TRIAL_SCHEDULED: 'Patient Logging Trial Scheduled',
  TAB_CHANGED: 'Tab Changed',
  PATIENT_NOTE_SAVED: 'Patient Note Saved',
  AGP_DATE_SELECTION: 'AGP Date Selected',
  PATIENT_TIP_SENT: 'Patient Tip Sent',

  // Food log + meals
  FOOD_LOG_FILTER_USED: 'Food Log Filter Used',
  MEAL_CARD_CLICKED: 'Meal Card Clicked',
  MEAL_ADDED: 'Meal Added',
  MEAL_EDITED: 'Meal Edited',
  MEAL_COPIED: 'Meal Copied',
  MEAL_DELETED: 'Meal Deleted',

  // Goals
  GOAL_DELETED: 'Goal Deleted',
  GOAL_EDITED: 'Goal Edited',
  GOAL_ADDED: 'Goal Added',

  // Nutrients
  NUTRIENT_DATE_CHANGED: 'Nutrition Date Range Changed',
  NUTRIENT_ROW_SELECTED: 'Nutrition Row Selected',
  MEAL_HISTORY_REPORT_DOWNLOADED: 'Meal History Report Downloaded',

  // Recipes
  RECIPE_SEARCH_SUBMITTED: 'Recipes Search Submitted',
  RECIPE_LIST_EMAIL_SENT: 'Recipe List Email Sent',

  // Reports
  DRAFT_REPORT_GENERATED: 'Draft Report Generated',
  DRAFT_REPORT_REGENERATED: 'Draft Report Regenerated',
  DRAFT_REPORT_DELETED: 'Draft Report Deleted',
  DRAFT_REPORT_SENT: 'Draft Report Sent',
  REPORT_VIEWED: 'Report Viewed',
  CLINICAL_SUMMARY_CLICKED: 'Clinical Summary Clicked',
  REPORT_LINK_SENT: 'Report Link Sent',
} as const;

/**
 * Identify the clinician to both Sentry and Countly. Runs the change_id
 * exactly once per id transition so a re-render doesn't churn Countly's
 * session merge logic. On logout (clinician=null) it clears Sentry's
 * user/tag context and resets Countly's device id back to anonymous —
 * the latter also fires when a stale id is detected even on first run,
 * which covers the "user navigated directly to /login while a previous
 * session's device id was still in localStorage" case.
 *
 * Clinics are optional — when supplied (e.g. from `useClinicianClinics`),
 * the resolved id list is forwarded as a tag on Sentry and a profile
 * property on Countly so cross-clinic-segmentation reports work.
 *
 * Mount once inside the authed layout (PageLayout is the canonical
 * spot). Mirrors v1's `useAnalyticsUserIdentity`.
 */
export const useAnalyticsUserIdentity = (
  clinician: Clinician | null | undefined,
  clinics?: ClinicResponse[] | null,
): void => {
  const prevIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!clinician?.id) {
      Sentry.setUser(null);
      Sentry.setTag('clinician_id', null);
      Sentry.setTag('clinic_ids', null);
      // Reset if we previously had a user OR if Countly's persisted
      // device id is non-anonymous from a prior session.
      if (prevIdRef.current !== null || hasStaleUserSession()) {
        resetUser();
        prevIdRef.current = null;
      }
      return;
    }
    const id = clinician.id;
    Sentry.setUser({ id: String(id), username: `User ${id}` });
    Sentry.setTag('clinician_id', String(id));
    const clinicIds = (clinics ?? [])
      .map((c) => String(c.id))
      .join(',');
    Sentry.setTag('clinic_ids', clinicIds || null);

    if (prevIdRef.current !== id) {
      prevIdRef.current = id;
      identifyUser(String(id), true, {
        name: `Clinician ${id}`,
        clinic_ids: clinicIds,
      });
    }
  }, [clinician?.id, clinics]);
};

/**
 * Per-route Sentry tag + breadcrumb. Mount once with the current
 * pathname (PageLayout's `useLocation()` is the natural spot). Helps
 * Sentry issues identify which page surfaced an error without having
 * to dig into request headers.
 *
 * The raw pathname embeds patient ids (e.g. `/patients/12345/summary`),
 * so we run it through `parameterizePath` first — the same PHI-scrubbing
 * helper Countly view tracking uses — before it reaches Sentry's tag or
 * breadcrumb trail. Never forward the un-parameterized path.
 */
export const useSentryRouteTracking = (pathname: string): void => {
  useEffect(() => {
    const route = parameterizePath(pathname);
    Sentry.setTag('route', route);
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${route}`,
      level: 'info',
    });
  }, [pathname]);
};
